import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import Tooltip from "react-tooltip";
import { MdModeEdit, MdFileDownload } from "react-icons/md";

import Switch from "../../../components/Switch";
import api from "../../../services/api";
import { classNames } from "../../../utils";

export default function Projects() {
  const [filters, setFilters] = useState({ inactive: false, year: "", projectStatus: "active" });
  const [budgets, setBudgets] = useState([]);
  const [activities, setActivities] = useState([]);
  const [banks, setBanks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    const query = { per_page: 20000 };
    if (!filters.inactive) query.status = "active";
    if (filters.year) query.year = filters.year;
    if (filters.projectStatus) query.projectStatus = filters.projectStatus;

    const { ok, data } = await api.post(`/budget/search`, query);
    if (!ok) {
      toast.error("Failed to fetch budgets");
      setLoading(false);
      return;
    }
    setBudgets(data);
    setLoading(false);
  };

  useEffect(() => {
    if (!budgets.length) return;
    
    const fetchRelatedData = async () => {
      const budgetIds = budgets.map((e) => e._id);

      const queryA = { budgetIds, per_page: 20000 };
      const { data: a } = await api.post(`/activity/search`, queryA);
      setActivities(a || []);

      const queryB = { budgetIds, notCategories: ["INCOME", "DISBURSEMENT"], per_page: 20000 };
      const { data: b } = await api.post(`/bank/search`, queryB);
      setBanks(b?.banks?.map((bank) => ({ ...bank, amount: bank.amount })) || []);

      const queryI = { budgetIds, per_page: 20000 };
      const { data: i } = await api.post(`/invoice/search`, queryI);
      setInvoices(i || []);
    };
    
    fetchRelatedData();
  }, [budgets]);

  if (loading) return <div className="p-4 bg-white">Loading...</div>;

  const totalAmount = budgets.filter((e) => e?.amount).reduce((acc, budget) => acc + budget.amount, 0);
  const totalRealUserCost = activities.reduce((acc, activity) => acc + activity.cost, 0);
  const totalBankCost = banks.filter((bank) => bank.category !== "INCOME" && bank.category !== "DISBURSEMENT").reduce((acc, bank) => acc - bank.amount, 0);
  const totalAmountUsed = budgets.filter((e) => e?.amountUsed).reduce((acc, budget) => acc + budget.amountUsed, 0);
  const totalAmountRemaining = totalAmount - totalAmountUsed;
  const totalAmountInvoiced = invoices.reduce((acc, invoice) => acc + invoice.netTotal, 0);

  const rows = [];
  for (let i = 0; i < budgets.length; i++) {
    const budget = budgets[i];
    const findActivities = activities.filter((activity) => activity.budgetId === budget._id);
    const findBanks = banks.filter((bank) => bank.budgetId === budget._id);
    const findInvoices = invoices.filter((invoice) => invoice.budgetId === budget._id);
    rows.push({ budget, invoices: findInvoices, banks: findBanks, activities: findActivities });
  }

  const computeProfit = (item) => {
    const invoiced = item.invoices.reduce((acc, cur) => acc + cur.netTotal, 0);
    const userCost = item.activities.reduce((acc, cur) => acc + cur.cost, 0);
    const bankCost = item.banks.filter((bank) => bank.category !== "INCOME" && bank.category !== "DISBURSEMENT").reduce((acc, bank) => acc - bank.amount, 0);
    const realCost = userCost + bankCost;

    if (item.budget.status === "inactive" || (item.budget.type !== "project" && item.budget.type !== "anticipation")) return invoiced - realCost;
    if (item.budget.amountUsed > item.budget.amount) return item.budget.amount - realCost;
    return item.budget.amountUsed - realCost;
  };

  const totalProfit = rows.reduce((acc, cur) => acc + computeProfit(cur), 0);

  const exportToCSV = () => {
    const headers = [
      "Project Name",
      "Project Status",
      "Budget Name",
      "Budget Status",
      "Type",
      "Start Date",
      "End Date",
      "Amount",
      "Amount Used",
      "Amount Used %",
      "User Cost",
      "Bank Cost",
      "Real Cost",
      "Invoiced",
      "Profitability",
      "Profitability %",
    ];

    const csvRows = rows.map((row) => {
      const userCost = row.activities.reduce((acc, cur) => acc + cur.cost, 0);
      const bankCost = row.banks.filter((bank) => bank.category !== "INCOME" && bank.category !== "DISBURSEMENT").reduce((acc, bank) => acc - bank.amount, 0);
      const realCost = userCost + bankCost;
      const invoiced = row.invoices.reduce((acc, cur) => acc + cur.netTotal, 0);
      const profit = computeProfit(row);
      const profitPercent = profit && row.budget.amount ? ((profit / (row.budget.amountUsed > row.budget.amount ? row.budget.amount : row.budget.amountUsed)) * 100).toFixed(2) : 0;
      const amountUsedPercent = row.budget.amount && row.budget.amountUsed ? ((row.budget.amountUsed / row.budget.amount) * 100).toFixed(2) : 0;

      return [
        row.budget.projectName || "",
        row.budget.projectStatus || "",
        row.budget.name || "",
        row.budget.status || "",
        row.budget.type || "",
        row.budget.startAt ? new Date(row.budget.startAt).toLocaleDateString("fr-FR") : "",
        row.budget.endAt ? new Date(row.budget.endAt).toLocaleDateString("fr-FR") : "",
        row.budget.amount || 0,
        row.budget.amountUsed || 0,
        amountUsedPercent,
        userCost,
        bankCost,
        realCost,
        invoiced,
        profit,
        profitPercent,
      ];
    });

    const totalsRow = [
      "TOTAL",
      "",
      "",
      "",
      "",
      "",
      "",
      totalAmount,
      totalAmountUsed,
      totalAmount && totalAmountUsed ? ((totalAmountUsed / totalAmount) * 100).toFixed(2) : 0,
      totalRealUserCost,
      totalBankCost,
      totalBankCost + totalRealUserCost,
      totalAmountInvoiced,
      totalProfit,
      totalProfit && totalAmount ? ((totalProfit / (totalAmountUsed > totalAmount ? totalAmount : totalAmountUsed)) * 100).toFixed(2) : 0,
    ];

    const csvContent = [
      headers.join(","),
      ...csvRows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      totalsRow.map((cell) => `"${cell}"`).join(","),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `global-projects-budget-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV exported successfully");
  };

  return (
    <div className="space-y-4 py-6 bg-white p-4">
      <div className="flex items-center gap-3 justify-between px-3">
        <h2 className="text-2xl font-bold">Global Projects Budget View</h2>
        <div className="flex items-center gap-3">
          <button onClick={exportToCSV} className="blue-btn flex items-center gap-2">
            <MdFileDownload className="text-lg" />
            Export CSV
          </button>
          <SelectYear value={filters.year} onChange={(year) => setFilters({ ...filters, year })} />
          <SelectProjectStatus value={filters.projectStatus} onChange={(projectStatus) => setFilters({ ...filters, projectStatus })} />
          <label className="text-sm font-semibold">Show Inactive Budgets</label>
          <Switch checked={filters.inactive} onChange={() => setFilters({ ...filters, inactive: !filters.inactive })} />
        </div>
      </div>

      <div className="flex items-center gap-3 px-3">
        <h3 className="text-lg font-semibold">{budgets.length} budgets found</h3>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="bg-white border border-gray-200 rounded p-3 flex-1">
          <h2 className="text-base font-semibold">Total amount</h2>
          <p className="text-lg font-bold">{(totalAmount || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded p-3 flex-1">
          <h2 className="text-base font-semibold">Total amount used</h2>
          <p className={`text-lg font-bold ${totalAmountUsed > totalAmount ? "text-red-700" : "text-green-700"}`}>
            {(totalAmountUsed || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded p-3 flex-1">
          <h2 className="text-base font-semibold">Total amount remaining</h2>
          <p className={`text-lg font-bold ${totalAmountRemaining < 0 ? "text-red-700" : "text-green-700"}`}>
            {(totalAmountRemaining || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded p-3 flex-1">
          <h2 className="text-base font-semibold">Total amount invoiced</h2>
          <p className={`text-lg font-bold ${totalAmountInvoiced < 0 ? "text-red-700" : "text-green-700"}`}>
            {(totalAmountInvoiced || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded p-3 flex-1">
          <h2 className="text-base font-semibold">Total profit</h2>
          <p className={`text-lg font-bold ${totalProfit < 0 ? "text-red-700" : "text-green-700"}`}>
            {(totalProfit || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
          </p>
        </div>
      </div>

      <div className="w-full bg-white border border-gray-300 rounded">
        <table className="table-fixed w-full text-sm">
          <thead className="border-b border-gray-300">
            <tr>
              <th className="text-left font-semibold text-sm p-3 w-1/6">Project</th>
              <th className="text-left font-semibold text-sm p-3 w-1/6">Budget</th>
              <th className="text-left font-semibold text-sm px-3 w-1/12">Type</th>
              <th className="text-left font-semibold text-sm p-2 w-1/12">Amount</th>
              <th className="text-left font-semibold text-sm px-3 w-1/12">Amount used</th>
              <th className="text-left font-semibold text-sm px-3 w-1/12">Real cost</th>
              <th className="text-left font-semibold text-sm px-3 w-1/12">Invoices</th>
              <th className="text-left font-semibold text-sm pr-10 w-1/12">Profitability</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <BudgetRow key={index} index={index} item={row} profit={computeProfit(row)} />
            ))}
          </tbody>
          <tfoot className="border-t border-gray-300">
            <tr>
              <td className="p-2 text-sm font-semibold" colSpan="3">Total</td>
              <td className="text-left p-2 text-sm font-semibold">{(totalAmount || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
              <td className="text-left p-2 text-sm font-semibold">
                {(totalAmountUsed || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}
                <div className="text-xs text-gray-600">{totalAmount && totalAmountUsed ? ((totalAmountUsed / totalAmount) * 100).toFixed(2) : 0}%</div>
              </td>
              <td className="text-left p-2 text-sm font-semibold">
                {(totalBankCost + totalRealUserCost || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}
                <div className="text-xs text-gray-600">User : {(totalRealUserCost || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</div>
                <div className="text-xs text-gray-600">Bank : {(totalBankCost || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</div>
              </td>
              <td className="text-left p-2 text-sm font-semibold">{(totalAmountInvoiced || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</td>
              <td className="text-left p-2 text-sm font-semibold">
                {(totalProfit || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}
                <div className="text-xs text-gray-600">
                  {totalProfit && totalAmount ? ((totalProfit / (totalAmountUsed > totalAmount ? totalAmount : totalAmountUsed)) * 100).toFixed(2) : 0}%
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

const SelectYear = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="block px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary">
    <option value="">All Years</option>
    <option value="2025">2025</option>
    <option value="2024">2024</option>
    <option value="2023">2023</option>
    <option value="2022">2022</option>
    <option value="2021">2021</option>
  </select>
);

const SelectProjectStatus = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="block px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary">
    <option value="">All Projects</option>
    <option value="active">Active Projects</option>
    <option value="inactive">Inactive Projects</option>
  </select>
);

const BudgetRow = ({ item, index, profit }) => {
  const invoiced = item.invoices.reduce((acc, cur) => acc + cur.netTotal, 0);
  const userCost = item.activities.reduce((acc, cur) => acc + cur.cost, 0);
  const bankCost = item.banks.filter((bank) => bank.category !== "INCOME" && bank.category !== "DISBURSEMENT").reduce((acc, bank) => acc - bank.amount, 0);
  const realCost = userCost + bankCost;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getBudgetTypeTag = (type, confidence) => {
    const typeClasses = {
      prediction: "bg-blue-100 text-blue-800",
      project: "bg-green-100 text-green-800",
      overbudget: "bg-red-100 text-red-800",
      admin: "bg-purple-100 text-purple-800",
      invest: "bg-indigo-100 text-indigo-800",
    };

    const needsConfidence = ["prediction"].includes(type);
    const confidenceValue = confidence || 0;

    return (
      <div className="flex flex-col gap-1">
        <span className={`capitalize px-2 py-1 rounded-full text-xs font-medium ${typeClasses[type] || "bg-gray-100 text-gray-800"}`}>{type}</span>
        {needsConfidence && (
          <span
            className={`text-xs font-medium ${
              confidenceValue >= 75 ? "text-green-500" : confidenceValue >= 50 ? "text-yellow-500" : confidenceValue >= 25 ? "text-orange-500" : "text-red-500"
            }`}>
            {confidenceValue.toFixed(0)}%
          </span>
        )}
      </div>
    );
  };

  return (
    <tr className={classNames("border-t border-gray-200", item.budget.status === "inactive" && "opacity-50")}>
      <td className="text-left p-2 text-sm">
        <Link to={`/project/${item.budget.projectId}/budget`} className="font-semibold hover:underline text-blue-600">
          {item.budget.projectName}
        </Link>
        {item.budget.projectStatus === "inactive" && <span className="ml-2 border border-gray-500 rounded px-1 text-gray-500 text-xs">Inactive</span>}
      </td>
      <td className="text-left p-2 text-sm">
        <div>
          <p className="font-semibold">{item.budget.name}</p>
          {item.budget.status === "inactive" && <span className="border border-gray-500 rounded px-1 text-gray-500 text-xs">Inactive</span>}
          <p className="text-xs text-gray-600">
            {formatDate(item.budget.startAt)} - {formatDate(item.budget.endAt)}
          </p>
        </div>
      </td>
      <td className="p-2 text-sm">{item.budget.type && getBudgetTypeTag(item.budget.type, item.budget.confidence)}</td>
      <td className="text-left p-2 text-sm font-medium">{(item.budget.amount || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
      <td className="text-left p-2 text-sm">
        <div className="font-medium">{(item.budget.amountUsed || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
        <div className="text-xs text-gray-600">{item.budget.amount && item.budget.amountUsed ? ((item.budget.amountUsed / item.budget.amount) * 100).toFixed(2) : 0}%</div>
      </td>
      <td className="text-left p-2 text-sm">
        <div className="font-medium">{(realCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
        <div className="text-xs text-gray-600">User : {(userCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
        <div className="text-xs text-gray-600">Bank : {(bankCost || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
      </td>
      <td className="text-left p-2 text-sm">
        <div className="font-medium">{(invoiced || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
        <div className="flex-1 flex flex-col gap-1 mt-1">
          {item.invoices.map((e, i) => (
            <div key={i} className="flex items-center gap-1 text-xs">
              <Link to={`/invoice/${e._id}`} target="_blank" className="truncate underline">
                <Tooltip border borderColor="#e6e6e6" id={`invoice-${e._id}`} effect="solid" className="max-w-xs text-sm keepOpacity" backgroundColor="#fff" textColor="#000">
                  {e.total.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
                </Tooltip>
                <span data-tip data-for={`invoice-${e._id}`}>
                  {e.name}
                </span>
              </Link>
              {e.sent === "yes" ? (
                <span className="border border-green-500 rounded text-green-500 px-1 text-xs truncate">Sent</span>
              ) : (
                <span className="border border-gray-500 rounded text-gray-500 px-1 text-xs truncate">Not sent</span>
              )}
              {e.received === "yes" ? (
                <span className="border border-green-500 rounded text-green-500 px-1 text-xs truncate">Received</span>
              ) : e.sent === "yes" ? (
                <span className="border border-red-500 rounded text-red-500 px-1 text-xs truncate">Not received</span>
              ) : (
                ""
              )}
            </div>
          ))}
        </div>
      </td>
      <td className="text-left p-2 text-sm">
        <div className="font-medium">{(profit || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</div>
        <div className="text-xs text-gray-600">
          {profit && item.budget.amount ? ((profit / (item.budget.amountUsed > item.budget.amount ? item.budget.amount : item.budget.amountUsed)) * 100).toFixed(2) : 0}%
        </div>
      </td>
    </tr>
  );
};
